import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import s from '../Themes.module.css';
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";
import { getThemes, getThemesSync } from '../services/themesCache';

export const Themes = () => {
  const { id } = useParams();
  const { user, handleLang } = useAuth();
  const { t } = useAppTranslation();

  const cachedRaw = getThemesSync(id);
  const initialThemes = cachedRaw
    ? cachedRaw.map(i => ({
        tartib: i.tartib, id: i.id, kurs_id: id,
        title_uz: i.nomi_uz, title_ru: i.nomi_ru, title_en: i.nomi_en,
        image: i.mrasmi_uz,
        desc_uz: i.short_text_uz, desc_ru: i.short_text_ru, desc_en: i.short_text_en,
        test_soni: i.test_soni, test_min: i.test_min_soni,
        test_description_uz: i.test_description_uz,
        test_description_ru: i.test_description_ru,
        test_description_en: i.test_description_en,
      })).sort((a, b) => a.tartib - b.tartib)
    : [];

  const [themes, setThemes]           = useState(initialThemes);
  const [kursNameUz, setKursNameUz]   = useState('');
  const [kursNameRu, setKursNameRu]   = useState('');
  const [kursNameEn, setKursNameEn]   = useState('');
  const [kursImg, setKursImg]         = useState('');
  const [loadingThemes, setLoadingThemes] = useState(!cachedRaw);
  const [errorThemes, setErrorThemes] = useState(null);
  const [openIndex, setOpenIndex]     = useState(null);
  const [userKurs, setUserKurs]       = useState(null);
  const [dostup, setDostup]           = useState(false);

  /* Загрузка инфо курса */
  useEffect(() => {
    if (!id) return;
    fetch(`https://api.aiassessment.uz/api/kurslar/${id}/`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        setKursNameUz(data.nomi_uz);
        setKursNameRu(data.nomi_ru);
        setKursNameEn(data.nomi_en);
        setKursImg(data.img_uz);
      })
      .catch(err => console.error("Ошибка загрузки курса:", err));
  }, [id]);

  /* Загрузка тем (кешированная) */
  useEffect(() => {
    if (!id || cachedRaw) return;
    getThemes(id)
      .then(json => {
        setThemes(
          json.map(i => ({
            tartib: i.tartib, id: i.id, kurs_id: id,
            title_uz: i.nomi_uz, title_ru: i.nomi_ru, title_en: i.nomi_en,
            image: i.mrasmi_uz,
            desc_uz: i.short_text_uz, desc_ru: i.short_text_ru, desc_en: i.short_text_en,
            test_soni: i.test_soni, test_min: i.test_min_soni,
            test_description_uz: i.test_description_uz,
            test_description_ru: i.test_description_ru,
            test_description_en: i.test_description_en,
          })).sort((a, b) => a.tartib - b.tartib)
        );
      })
      .catch(err => setErrorThemes(err.message))
      .finally(() => setLoadingThemes(false));
  }, [id]);

  /* Загрузка user-kurs и dostup */
  useEffect(() => {
    const token  = localStorage.getItem('token');
    const userId = user?.id;
    if (!token || !userId) return;

    fetch(`https://api.aiassessment.uz/api/user-kurs/?user=${userId}&kurs=${id}`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setUserKurs(data?.[0]))
      .catch(err => console.error("Ошибка user-kurs:", err));

    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const gruhId = localUser?.gruh;
    if (gruhId) {
      fetch(`https://api.aiassessment.uz/api/gruh/${gruhId}/`)
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(data => setDostup(data.dostup))
        .catch(() => {});
    }
  }, [user, id]);

  const toggle = (index) => setOpenIndex(openIndex === index ? null : index);

  /* ── States ── */
  if (errorThemes) return <p style={{ padding: '80px' }}>Ошибка: {errorThemes}</p>;

  if (loadingThemes || !userKurs) return (
    <div className={s.page}>
      <div className={s.loading}>
        <div className={s.loadingBar} style={{ width: '40%' }} />
        <div className={s.loadingBar} />
        <div className={s.loadingBar} />
        <div className={s.loadingBar} />
      </div>
    </div>
  );

  const completed   = userKurs.mavzu;
  const progressPct = Math.min(100, Math.round((completed / themes.length) * 100));
  const courseName  = handleLang(kursNameUz, kursNameRu, kursNameEn);

  return (
    <div className={s.page}>

      {/* ── HEADER ── */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <span className={s.headerLabel}>{t('themes') || 'Темы курса'}</span>
          <h1 className={s.headerTitle}>{courseName}</h1>

          <div className={s.progressRow}>
            <span className={s.progressNum}>
              {Math.min(completed, themes.length)}/{themes.length}
            </span>
            <div className={s.progressBar}>
              <div className={s.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
            <span className={s.progressText}>{t('completed') || 'пройдено'}</span>
          </div>
        </div>

        {kursImg && (
          <img src={kursImg} alt="" className={s.headerImage} />
        )}
      </div>

      {/* ── THEMES LIST ── */}
      <div className={s.content}>
        <div className={s.listHeader}>
          <span className={s.listTitle}>{t('programTitle') || 'Программа курса'}</span>
          <span className={s.listCount}>{themes.length} {t('themes') || 'тем'}</span>
        </div>

        {themes.map((item, index) => {
          const isAccessible = item.tartib <= completed;
          const isOpen       = openIndex === index;

          return (
            <div
              key={item.id}
              className={`${s.item} ${isAccessible ? '' : s.itemLocked}`}
              style={{ animationDelay: `${index * 0.04}s` }}
            >
              {/* Header row */}
              <div className={s.itemHeader} onClick={() => isAccessible && toggle(index)}>
                <div className={s.itemNum}>
                  {String(index + 1).padStart(2, '0')}
                </div>

                <span className={s.itemTitle}>
                  {handleLang(item.title_uz, item.title_ru, item.title_en)}
                </span>

                <div className={s.itemRight}>
                  {isAccessible && item.tartib < completed && (
                    <span className={s.statusDone}>✓</span>
                  )}
                  {!isAccessible && <span className={s.statusLock}>🔒</span>}
                  {isAccessible && (
                    <div className={`${s.chevron} ${isOpen ? s.chevronOpen : ''}`} />
                  )}
                </div>
              </div>

              {/* Body — opens on click */}
              <div className={`${s.itemBody} ${isOpen ? s.itemBodyOpen : ''}`}>
                {isAccessible ? (
                  <Link className={s.lessonLink} to={`/Lesson/${id}/${item.id}`}>
                    {item.image && (
                      <img src={item.image} className={s.lessonThumb} alt="" />
                    )}
                    <p className={s.lessonDesc}>
                      {handleLang(item.desc_uz, item.desc_ru, item.desc_en)}
                    </p>
                  </Link>
                ) : (
                  <div className={s.lessonLocked}>
                    {item.image && (
                      <img src={item.image} className={s.lessonThumb} alt="" />
                    )}
                    <p className={s.lessonDesc}>
                      {handleLang(item.desc_uz, item.desc_ru, item.desc_en)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* ── Final exam ── */}
        {(() => {
          const finalAccessible = themes.length + 1 === completed && dostup;
          return (
            <div className={s.finalItem}>
              {finalAccessible ? (
                <Link className={`${s.finalHeader} ${s.finalActive}`} to={`/Yakuniy/${id}/`}>
                  <div className={s.finalNum}>★</div>
                  <span className={s.itemTitle}>{t('themesFin') || 'Yakuniy test'}</span>
                  <div className={s.chevron} />
                </Link>
              ) : (
                <div className={`${s.finalHeader} ${s.finalLocked}`}>
                  <div className={s.finalNum} style={{ background: '#E5E5E0' }}>★</div>
                  <span className={s.itemTitle} style={{ color: '#bbb' }}>
                    {t('themesFin') || 'Yakuniy test'}
                  </span>
                  <span className={s.statusLock}>🔒</span>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};
